import { isEqual, keyBy } from 'lodash-es';
import type { Vector3Tuple } from 'three';

import type { BoxWithLabel } from '@/type';

export const setBoxBuffer = (buf: Float32Array, [d, h, w]: Vector3Tuple) => {
  buf[0] = buf[9] = buf[12] = buf[15] = -w;
  buf[3] = buf[6] = buf[18] = buf[21] = w;

  buf[1] = buf[4] = buf[13] = buf[22] = -h;
  buf[7] = buf[10] = buf[16] = buf[19] = h;

  buf[14] = buf[17] = buf[20] = buf[23] = -d;
  buf[2] = buf[5] = buf[8] = buf[11] = d;
};

const normalizeRotation = (value: number): number => {
  const TWO_PI = 2 * Math.PI;
  return ((value % TWO_PI) + TWO_PI) % TWO_PI;
};

const BOX_VALUE_DIFF_KEY_LIST = [
  'dimension',
  'position',
  'rotation',
] as (keyof Pick<BoxWithLabel, 'dimension' | 'position' | 'rotation'>)[];

export const isEqualBoxList = (
  aList: Omit<Omit<BoxWithLabel, 'workType'>, 'quaternion'>[],
  bList: Omit<Omit<BoxWithLabel, 'workType'>, 'quaternion'>[],
) => {
  [aList, bList] =
    aList.length >= bList.length ? [aList, bList] : [bList, aList];
  const bMap = keyBy(bList, v => v.id);

  return aList.every(a => {
    const bBox = bMap[a.id];

    return bBox
      ? BOX_VALUE_DIFF_KEY_LIST.reduce((acc, key) => {
          if (!acc) return false;

          if (key === 'rotation') {
            return isEqual(
              (a[key].slice(0, 3) as Vector3Tuple).map(v =>
                normalizeRotation(v)?.toFixed(5),
              ),
              (bMap[a.id][key].slice(0, 3) as Vector3Tuple).map(v =>
                normalizeRotation(v)?.toFixed(5),
              ),
            );
          }

          return isEqual(
            a[key].map(v => v?.toFixed(5)),
            bMap[a.id][key].map(v => v?.toFixed(5)),
          );
        }, true) &&
          Number(a.label) === Number(bMap[a.id].label) &&
          Number(a.confidence) === Number(bMap[a.id].confidence) &&
          a.trackId === bMap[a.id].trackId
      : false;
  });
};
